/* The document queue filters, shared by the console and the portal. No API hooks.
 *
 * Input:  the current filters, an onChange patch callback, onClear, and optionally the
 *         per-stage counts (shown in the Stage dropdown) and a company picker node.
 * Output: a labelled grid - direction, search, stage, state, family (outbound only), type,
 *         then company, from, to, clear - where every control takes one equal column so
 *         the rows line up.
 */

import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { Icon } from "../../design/components";
import type { StageCounts } from "../../api/types";
import { stageLabel, stripStages } from "./StageStrip";
import {
  DIRECTIONS,
  FAMILY_OPTIONS,
  QUERY_DEBOUNCE_MS,
  STATE_OPTIONS,
  TYPE_OPTIONS,
  type QueueFilters,
} from "./queueFilterState";
import styles from "./DocumentsQueue.module.css";

/** A grid cell with a visible caption; the control keeps its own accessible name. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel} aria-hidden="true">
        {label}
      </span>
      {children}
    </div>
  );
}

export function FilterControls({
  filters,
  onChange,
  onClear,
  hasFilters,
  stageCounts,
  company,
}: {
  filters: QueueFilters;
  onChange: (patch: Partial<QueueFilters>) => void;
  onClear: () => void;
  hasFilters: boolean;
  stageCounts?: StageCounts | undefined;
  company?: ReactNode;
}) {
  const { t } = useTranslation();
  const [localQ, setLocalQ] = useState(filters.q);

  useEffect(() => setLocalQ(filters.q), [filters.q]);

  useEffect(() => {
    if (localQ === filters.q) return;
    const timer = window.setTimeout(() => onChange({ q: localQ }), QUERY_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localQ]);

  const count = (stage: string) => (stageCounts ? ` (${stageCounts[stage] ?? 0})` : "");
  const date = (key: "from" | "to", name: string) => (
    <input
      type="date"
      className="input input-sm"
      aria-label={name}
      value={filters[key]}
      onChange={(event) => onChange({ [key]: event.target.value })}
    />
  );

  return (
    <div className={styles.filterGrid}>
      <Field label={t("documents.filters.direction", "Direction")}>
        <div className={styles.seg} role="group" aria-label={t("documents.filters.direction", "Direction")}>
          {DIRECTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className="btn btn-sm"
              aria-pressed={filters.direction === option.value}
              onClick={() => onChange({ direction: option.value })}
            >
              {option.label}
            </button>
          ))}
        </div>
      </Field>

      <Field label={t("documents.filters.search", "Search")}>
        <div className="search">
          <Icon name="search" />
          <input
            className="input input-sm"
            placeholder={t("documents.filters.searchPlaceholder", "ERP document ID or UUID")}
            aria-label={t("documents.filters.searchAriaLabel", "Search documents")}
            value={localQ}
            onChange={(event) => setLocalQ(event.target.value)}
          />
        </div>
      </Field>

      <Field label={t("documents.filters.stage", "Stage")}>
        <select
          className="select select-sm"
          aria-label={t("documents.filters.stageAriaLabel", "Filter by stage")}
          value={filters.stage[0] ?? ""}
          onChange={(event) => onChange({ stage: event.target.value ? [event.target.value] : [] })}
        >
          <option value="">{t("documents.filters.allStages", "All stages")}</option>
          {stripStages(filters.direction).map((stage) => (
            <option key={stage} value={stage}>
              {`${stageLabel(stage, filters.direction)}${count(stage)}`}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t("documents.filters.state", "State")}>
        <select
          className="select select-sm"
          aria-label={t("documents.filters.stateAriaLabel", "Filter by state")}
          value={filters.state}
          onChange={(event) => onChange({ state: event.target.value })}
        >
          <option value="">{t("documents.filters.allStates", "All states")}</option>
          {STATE_OPTIONS[filters.direction].map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </Field>

      {filters.direction === "out" ? (
        <Field label={t("documents.filters.family", "Family")}>
          <select
            className="select select-sm"
            aria-label={t("documents.filters.familyAriaLabel", "Filter by family")}
            value={filters.family}
            onChange={(event) => onChange({ family: event.target.value })}
          >
            <option value="">{t("documents.filters.allFamilies", "All families")}</option>
            {FAMILY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>
      ) : null}

      <Field label={t("documents.filters.type", "Type")}>
        <select
          className="select select-sm"
          aria-label={t("documents.filters.typeAriaLabel", "Filter by type")}
          value={filters.typeCode[0] ?? ""}
          onChange={(event) => onChange({ typeCode: event.target.value ? [event.target.value] : [] })}
        >
          <option value="">{t("documents.filters.allTypes", "All types")}</option>
          {TYPE_OPTIONS[filters.direction].map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </Field>

      {company ? <Field label={t("documents.filters.company", "Company")}>{company}</Field> : null}
      <Field label={t("documents.filters.from", "From")}>{date("from", t("documents.filters.fromDate", "From date"))}</Field>
      <Field label={t("documents.filters.to", "To")}>{date("to", t("documents.filters.toDate", "To date"))}</Field>

      <div className={styles.clearCell}>
        {hasFilters ? (
          <button type="button" className="btn btn-sm btn-ghost" onClick={onClear}>
            {t("documents.filters.clearFilters", "Clear filters")}
          </button>
        ) : null}
      </div>
    </div>
  );
}
